import { Check, X } from "lucide-react"
import { useMemo, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { QualityProposal } from "@/lib/types"

interface Props {
  proposal: QualityProposal
  onAcceptAll: (acceptedFields: string[]) => void
}

// Pretty-print proposed field values for display. JSONB → readable string.
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "—"
  if (typeof value === "string") return value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function sourceLabel(source: string): string {
  if (source === "osm") return "OSM"
  if (source === "claude") return "Claude"
  if (source === "curator") return "Curator"
  return source
}

function sourceVariant(source: string): "default" | "secondary" | "outline" {
  if (source === "osm") return "secondary"
  if (source === "claude") return "default"
  return "outline"
}

export function FieldDiff({ proposal, onAcceptAll }: Props) {
  const fieldEntries = useMemo(
    () => Object.entries(proposal.fields ?? {}),
    [proposal.fields],
  )

  // Track per-field accept/reject state. Default = accepted (curator can untick).
  const initialAccepted = useMemo(() => {
    const map: Record<string, boolean> = {}
    for (const [name] of fieldEntries) map[name] = true
    return map
  }, [fieldEntries])

  const [accepted, setAccepted] = useState<Record<string, boolean>>(initialAccepted)

  const toggle = (name: string) =>
    setAccepted((prev) => ({ ...prev, [name]: !prev[name] }))

  const setAll = (value: boolean) => {
    const next: Record<string, boolean> = {}
    for (const [name] of fieldEntries) next[name] = value
    setAccepted(next)
  }

  const acceptedCount = Object.values(accepted).filter(Boolean).length

  const handleAcceptAll = () => {
    const acceptedNames = fieldEntries
      .filter(([name]) => accepted[name])
      .map(([name]) => name)
    onAcceptAll(acceptedNames)
  }

  if (fieldEntries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        AI proposal contained no field updates.
      </p>
    )
  }

  return (
    <div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Field</th>
              <th className="px-3 py-2 text-left font-medium">Proposed</th>
              <th className="px-3 py-2 text-left font-medium">Source</th>
              <th className="px-3 py-2 text-right font-medium">Accept</th>
            </tr>
          </thead>
          <tbody>
            {fieldEntries.map(([name, payload]) => {
              const isAccepted = accepted[name] ?? true
              return (
                <tr key={name} className="border-b last:border-b-0">
                  <td className="px-3 py-2 font-mono text-xs">{name}</td>
                  <td className="px-3 py-2 text-foreground">
                    {formatValue(payload.value)}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={sourceVariant(payload.source)} className="text-[10px]">
                      Source: {sourceLabel(payload.source)}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      variant={isAccepted ? "default" : "outline"}
                      onClick={() => toggle(name)}
                      aria-pressed={isAccepted}
                      aria-label={
                        isAccepted
                          ? `Mark ${name} as rejected`
                          : `Mark ${name} as accepted`
                      }
                      className="size-7 p-0"
                    >
                      {isAccepted ? (
                        <Check className="size-3.5" aria-hidden />
                      ) : (
                        <X className="size-3.5" aria-hidden />
                      )}
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => setAll(false)}>
          Reject all
        </Button>
        <Button size="sm" variant="outline" onClick={() => setAll(true)}>
          Mark all
        </Button>
        <Button size="sm" onClick={handleAcceptAll} disabled={acceptedCount === 0}>
          Apply {acceptedCount} {acceptedCount === 1 ? "field" : "fields"}
        </Button>
      </div>
    </div>
  )
}
